import moment from 'moment';
import React, { useEffect, useReducer, useState } from 'react';
import Form from 'react-bootstrap/Form';
import { Helmet } from 'react-helmet-async';
import { FaRegSave } from 'react-icons/fa';
import { MdArrowBack } from 'react-icons/md';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from '../../toasts';
import FormatedNumberInput from '../../components/FormatedNumberInput';
import DetailPageLoader from '../../components/DetailPageLoader/DetailPageLoader';
import { Button, EntityList, FormField, PageHead, Section, Select, controlClass, textareaClass } from '../../components/EntityList';
import debtService from '../../services/debt.service';
import generatedDebtService from '../../services/generatedDebts.service';
import { getError } from '../../utils';
import { formatDateString } from '../../components/FormattedDateTime/FormattedDateTime';
import { useLabels } from '../../hooks/useLabels';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loadingFetch: true, errorFetch: '' };
	case 'FETCH_SUCCESS':
		return { ...state, debt: action.payload, loadingFetch: false };
	case 'FETCH_FAIL':
		return { ...state, loadingFetch: false, errorFetch: action.payload };
	case 'CREATE_REQUEST':
		return { ...state, loadingCreate: true };
	case 'CREATE_SUCCESS':
	case 'CREATE_FAIL':
		return { ...state, loadingCreate: false };
	default:
		return state;
	}
};

const REQUIRED_FIELD_MESSAGE = 'Este campo es obligatorio.';

const FieldError = ({ message }) =>
	message ? <p className='tw:mt-1 tw:mb-0 tw:text-[12px] tw:text-danger'>{message}</p> : null;

FieldError.propTypes = {
	message: PropTypes.string,
};

function NewDebtDue() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { id } = useParams();
	const backTo = `/AdminScreen/debts/debt/${id}/cuotas`;

	const [amount, setAmount] = useState();
	const [date, setDate] = useState();
	const [customId, setCustomId] = useState('');
	const [expiration, setExpiration] = useState('');
	const [paid, setPaid] = useState('false');
	const [debtInit, setDebtInit] = useState();
	const [debtCloseDate, setDebtCloseDate] = useState();
	const [debtEndDate, setDebtEndDate] = useState();
	const [debtDueDay, setDebtDueDate] = useState();
	const [debtCustomId, setDebtCustomId] = useState();
	const [fieldErrors, setFieldErrors] = useState({});

	const [{ loadingFetch, loadingCreate, debt, errorFetch }, dispatch] = useReducer(reducer, {
		loadingFetch: true,
		debt: null,
		loadingCreate: false,
		errorFetch: '',
	});

	useEffect(() => {
		const fetchData = async () => {
			dispatch({ type: 'FETCH_REQUEST' });
			try {
				const result = await debtService.getDetails(id);
				setDebtDueDate(result.data.dueDay);
				setAmount(result.data.amount);
				setDebtCustomId(result.data.customId);
				setDebtInit(moment.utc(result.data.firstDueExpiration));
				if ((result.data.concept === 'prestamos-amortizable') || (result.data.concept === 'ventas-a-plazo')) {
					calculateDebtEnd(result.data.firstDueExpiration, result.data.periodicity, result.data.dues, result.data.numMonths);
				} else {
					setDebtCloseDate(result.data.closeDate);
					setDebtEndDate(result.data.endDate);
				}

				dispatch({ type: 'FETCH_SUCCESS', payload: result.data });
			} catch (error) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(error) });
			}
		};
		fetchData();
	}, [id]);

	const calculateDebtEnd = async (debtInitValue, debtIdPeriodicity, debtIdDues, debtIdnumMonths) => {
		let numMonths = 0;
		if (debtIdPeriodicity === 'mensual') {
			numMonths = 1;
		} else if (debtIdPeriodicity === 'semestral') {
			numMonths = 6;
		} else if (debtIdPeriodicity === 'anual') {
			numMonths = 12;
		} else if (debtIdPeriodicity === 'otra') {
			numMonths = debtIdnumMonths;
		}
		const totalMonthsToAdd = parseInt(debtIdDues) * numMonths;

		const calculatedEndDate = moment.utc(debtInitValue).add(totalMonthsToAdd, 'months');
		setDebtEndDate(moment.utc(calculatedEndDate).format('YYYY-MM'));
	};

	const clearFieldError = (field) => {
		setFieldErrors((prevErrors) => {
			if (!prevErrors[field]) return prevErrors;
			const nextErrors = { ...prevErrors };
			delete nextErrors[field];
			return nextErrors;
		});
	};

	const handleDateChange = (newDate) => {
		setDate(newDate);
		const expirationDate = moment.utc(newDate).date(debtDueDay);
		setExpiration(moment.utc(expirationDate).format('YYYY-MM-DD'));
		clearFieldError('date');
	};

	const FIELD_LABELS = {
		date: t('form.dueMonth'),
		amount: t('field.amount'),
		customId: t('form.detail'),
	};

	const getFieldErrors = () => {
		const errors = {};
		if (!date) errors.date = REQUIRED_FIELD_MESSAGE;
		if (!amount) errors.amount = REQUIRED_FIELD_MESSAGE;
		if (!customId || !customId.trim()) errors.customId = REQUIRED_FIELD_MESSAGE;
		return errors;
	};

	const addDueHandler = async () => {
		if (!debt) {
			toast.error(t('msg.selectDebtor'));
			return;
		}

		const errors = getFieldErrors();
		const missingFields = Object.keys(errors);
		if (missingFields.length > 0) {
			setFieldErrors(errors);
			const missingLabels = missingFields.map((field) => FIELD_LABELS[field] || field);
			toast.error(`Complete el/los siguiente/s campo/s requerido/s: ${missingLabels.join(', ')}.`);
			return;
		}
		setFieldErrors({});

		try {
			dispatch({ type: 'CREATE_REQUEST' });
			await generatedDebtService.create({
				amount,
				debt,
				date,
				customId,
				paid,
				expiration,
			});
			dispatch({ type: 'CREATE_SUCCESS' });
			toast.success(t('msg.dueCreated'));
			navigate(backTo);
		} catch (ex) {
			dispatch({ type: 'CREATE_FAIL' });
			toast.error(getError(ex));
		}
	};

	return (
		<EntityList>
			<Helmet>
				<title>{`${t('action.add')} ${t('entity.due')}`}</title>
			</Helmet>
			<PageHead
				breadcrumb={[
					{ label: 'Inicio', to: '/AdminScreen' },
					{ label: t('entity.debt.plural'), to: '/AdminScreen/debts' },
					{ label: debtCustomId || t('entity.debt'), to: backTo },
					{ label: `${t('action.add')} ${t('entity.due')}` },
				]}
				title={`${t('action.add')} ${t('entity.due')}`}
				subtitle='Completá los datos para agregar la cuota manual.'
			/>

			{loadingFetch ? (
				<DetailPageLoader entityLabel='editor' />
			) : (
				<Form id='debt-due-editor-form' onSubmit={(event) => { event.preventDefault(); addDueHandler(); }} className='tw:space-y-4'>
					<div className='tw:sticky tw:top-14 tw:z-20 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:rounded-panel tw:border tw:border-border tw:bg-surface/95 tw:px-5 tw:py-3.5 tw:shadow-sm tw:backdrop-blur'>
						<div className='tw:text-[12.5px] tw:text-ink-2'>
							Completá los datos para agregar la cuota manual.
						</div>
						<div className='tw:flex tw:gap-2'>
							<Button icon={<MdArrowBack />} onClick={() => navigate(backTo)} disabled={loadingCreate}>
								{t('action.cancel')}
							</Button>
							<Button variant='primary' icon={<FaRegSave />} type='submit' disabled={loadingCreate}>
								{loadingCreate ? 'Guardando...' : t('action.add')}
							</Button>
						</div>
					</div>

					{errorFetch && (
						<div className='tw:rounded-[7px] tw:border tw:border-danger tw:bg-danger-soft tw:px-4 tw:py-3 tw:text-[13px] tw:text-danger'>
							{errorFetch}
						</div>
					)}

					<Section title={t('entity.due')}>
						<div className='tw:grid tw:grid-cols-1 tw:gap-4 md:tw:grid-cols-2'>
							<FormField id='debt-due-debtor' label={t('entity.account.debtor')} required>
								<Form.Control id='debt-due-debtor' className={controlClass} value={debtCustomId || ''} disabled />
							</FormField>
							<FormField id='debt-due-amount' label={t('field.amount')} required>
								<FormatedNumberInput
									id='debt-due-amount'
									type='number'
									className={controlClass}
									value={amount || ''}
									onChange={(event) => {
										setAmount(event.target.value);
										clearFieldError('amount');
									}}
								/>
								<FieldError message={fieldErrors.amount} />
							</FormField>
							<FormField id='debt-due-month' label={t('form.dueMonth')} required>
								<Form.Control
									id='debt-due-month'
									type='month'
									lang='es'
									className={controlClass}
									onChange={(event) => handleDateChange(event.target.value)}
									min={moment.utc(debtInit).format('YYYY-MM')}
									max={
										debtCloseDate
											? moment.utc(debtCloseDate).format('YYYY-MM')
											: debtEndDate
												? moment.utc(debtEndDate).format('YYYY-MM')
												: undefined
									}
								/>
								<FieldError message={fieldErrors.date} />
							</FormField>
							<FormField id='debt-due-expiration' label={t('field.expirationDate')}>
								<Form.Control
									id='debt-due-expiration'
									type='text'
									className={controlClass}
									value={formatDateString({ dateValue: expiration, dateFormat: 'DD/MM/YYYY' })}
									disabled
								/>
							</FormField>
							<FormField id='debt-due-paid' label={t('form.paidQ')}>
								<Select
									id='debt-due-paid'
									options={[
										{ value: 'false', label: t('form.no') },
										{ value: 'true', label: t('form.yes') },
									]}
									value={paid}
									onChange={(event) => setPaid(event.target.value)}
								/>
							</FormField>
						</div>
					</Section>

					<Section title={t('form.detail')}>
						<FormField id='debt-due-detail' label={t('form.detail')} required>
							<Form.Control
								id='debt-due-detail'
								as='textarea'
								rows={8}
								className={textareaClass}
								value={customId}
								onChange={(event) => {
									setCustomId(event.target.value);
									clearFieldError('customId');
								}}
							/>
							<FieldError message={fieldErrors.customId} />
						</FormField>
					</Section>
				</Form>
			)}
		</EntityList>
	);
}

export default NewDebtDue;
